import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowLeft, CircleCheck, CircleX, Info } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { User } from '../../../../shared/types/user.types';
import { Vehicle, VehicleStatus } from '../../../../shared/types/vehicle.types';
import { vehicleStatusConfig } from '../../utils/vehicleStatus';
import useVehicle from '../../wrappers/use-vehicle';
import DocumentList from '../../components/DocumentList';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import {
    registrationDetailStyles,
    vehicleRegistrationRowStyles,
} from './VehicleRegistrationDetail.styles';

const RegistrationDetail: React.FC = () => {
    const { uid } = useParams<{ uid: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [customer, setCustomer] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const { get, edit } = useVehicle();

    useEffect(() => {
        if (!uid) return;
        const load = async () => {
            setLoading(true);
            const loadedVehicle = await get(uid);
            if (!loadedVehicle) {
                setLoading(false);
                return;
            }
            setVehicle(loadedVehicle);

            if (loadedVehicle.createdBy) {
                const userSnap = await getDoc(doc(firestore, 'user', loadedVehicle.createdBy));
                if (userSnap.exists()) {
                    const u = userSnap.data();
                    setCustomer({
                        uid: userSnap.id,
                        name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
                        firstName: u.firstName,
                        lastName: u.lastName,
                        email: u.email,
                        role: u.role,
                        garageUid: u.garageUid,
                        phone: u.phone,
                    });
                }
            }
            setLoading(false);
        };
        load();
    }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApprove = async () => {
        if (!uid || !vehicle) return;
        await edit(uid, { status: VehicleStatus.Active, model: vehicle.model, warranty: vehicle.warranty, statusNote: '' });
        navigate('/app/admin/vehicle-registrations');
    };

    const handleReject = async () => {
        if (!uid) return;
        await edit(uid, { status: VehicleStatus.Rejected, statusNote: note });
        setShowRejectModal(false);
        navigate('/app/admin/vehicle-registrations');
    };

    const handleRequestInfo = async () => {
        if (!uid) return;
        await edit(uid, { status: VehicleStatus.InfoRequested, statusNote: note });
        setShowInfoModal(false);
        navigate('/app/admin/vehicle-registrations');
    };

    if (loading) {
        return (
            <Box sx={registrationDetailStyles.loadingContainer}>
                <CircularProgress />
            </Box>
        );
    }

    if (!vehicle) {
        return (
            <Box sx={registrationDetailStyles.notFoundContainer}>
                <Typography color="text.secondary">{t('vehicleRegistrationDetail.notFound')}</Typography>
                <Button component={Link} to="/app/admin/vehicle-registrations" sx={registrationDetailStyles.notFoundButton}>
                    {t('vehicleRegistrationDetail.backToList')}
                </Button>
            </Box>
        );
    }

    const statusDisplay = vehicleStatusConfig[vehicle.status];
    const isPending = vehicle.status === VehicleStatus.Pending;
    const isModelValid = !!vehicle.model?.trim() && vehicle.model.trim() !== 'Neznámý model';
    const canApprove = isModelValid && !!vehicle.warranty?.type && !!vehicle.warranty?.validUntil;

    const actionButtons = isPending ? (
        <Box sx={registrationDetailStyles.actionButtons}>
            <Button variant="outlined" startIcon={<Info size={20} />} onClick={() => setShowInfoModal(true)}>
                {t('vehicleRegistrationDetail.requestInfo')}
            </Button>
            <Button variant="contained" color="error" startIcon={<CircleX size={20} />} onClick={() => setShowRejectModal(true)}>
                {t('vehicleRegistrationDetail.reject')}
            </Button>
            <Button variant="contained" color="success" startIcon={<CircleCheck size={20} />} onClick={handleApprove} disabled={!canApprove}>
                {t('vehicleRegistrationDetail.approve')}
            </Button>
        </Box>
    ) : null;

    const rows: { label: string; value: React.ReactNode }[] = [
        {
            label: t('vehicleRegistrationDetail.vinLabel'),
            value: (
                <Typography variant="body2" fontFamily="monospace" sx={vehicleRegistrationRowStyles.vinBadge}>
                    {vehicle.vin}
                </Typography>
            ),
        },
        {
            label: t('vehicleRegistrationDetail.modelLabel'),
            value: isPending ? (
                <TextField
                    size="small"
                    value={vehicle.model}
                    onChange={(e) => setVehicle((v) => v ? { ...v, model: e.target.value } : v)}
                    error={!isModelValid}
                    fullWidth
                />
            ) : vehicle.model,
        },
        { label: t('vehicleRegistrationDetail.purchaseDateLabel'), value: formatDate(vehicle.purchaseDate) },
        {
            label: t('vehicleRegistrationDetail.extendedWarrantyRequestLabel'),
            value: (
                <Typography
                    variant="body2"
                    fontWeight={600}
                    color={vehicle.extendedWarrantyRequested ? 'success.main' : 'text.secondary'}
                >
                    {vehicle.extendedWarrantyRequested ? t('common.yes') : t('common.no')}
                </Typography>
            ),
        },
        {
            label: t('vehicleRegistrationDetail.warrantyTypeLabel'),
            value: isPending ? (
                <FormControl size="small" sx={vehicleRegistrationRowStyles.warrantySelect} error={!vehicle.warranty?.type}>
                    <Select
                        value={vehicle.warranty?.type ?? ''}
                        onChange={(e) => {
                            const type = e.target.value;
                            const years = type === 'extended' ? 5 : 2;
                            const base = new Date(vehicle.purchaseDate);
                            base.setFullYear(base.getFullYear() + years);
                            const validUntil = base.toISOString().split('T')[0];
                            setVehicle((v) => v ? { ...v, warranty: { ...v.warranty, type, validUntil } } : v);
                        }}
                    >
                        <MenuItem value="standart">{t('vehicleRegistrationDetail.warrantyStandard')}</MenuItem>
                        <MenuItem value="extended">{t('vehicleRegistrationDetail.warrantyExtended')}</MenuItem>
                    </Select>
                </FormControl>
            ) : (vehicle.warranty?.type ?? ''),
        },
        {
            label: t('vehicleRegistrationDetail.warrantyUntilLabel'),
            value: isPending ? (
                <TextField
                    size="small"
                    type="date"
                    value={vehicle.warranty?.validUntil}
                    onChange={(e) => setVehicle((v) => v ? { ...v, warranty: { ...(v.warranty!), validUntil: e.target.value } } : v)}
                    error={!vehicle.warranty?.validUntil}
                    InputLabelProps={{ shrink: true }}
                />
            ) : (vehicle.warranty?.validUntil ? formatDate(vehicle.warranty.validUntil) : ''),
        },
        { label: t('vehicleRegistrationDetail.dealerLabel'), value: vehicle.dealer },
        {
            label: t('vehicleRegistrationDetail.customerLabel'),
            value: customer ? (
                <Box>
                    <Typography variant="body2">{customer.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{customer.email}</Typography>
                    {customer.phone && (
                        <Typography variant="body2" color="text.secondary">{customer.phone}</Typography>
                    )}
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
            ),
        },
        { label: t('vehicleRegistrationDetail.documentsLabel'), value: <DocumentList filters={{ vehicleUid: uid }} /> },
    ];

    return (
        <Box sx={registrationDetailStyles.root}>
            {/* Horní lišta */}
            <Box sx={registrationDetailStyles.header}>
                <Box sx={registrationDetailStyles.headerLeft}>
                    <IconButton component={Link} to="/app/admin/vehicle-registrations" size="small">
                        <ArrowLeft size={20} />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>{t('vehicleRegistrationDetail.title')}</Typography>
                </Box>
                {actionButtons}
            </Box>

            {/* Karta s detaily */}
            <Paper elevation={0} sx={registrationDetailStyles.card}>
                <Box sx={registrationDetailStyles.cardHeader}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{t('vehicleRegistrationDetail.sectionTitle')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('vehicleRegistrationDetail.sectionSubtitle')}</Typography>
                        {vehicle.statusNote && (
                            <Typography variant="body2" color={vehicle.status === VehicleStatus.Rejected ? 'error.main' : 'info.main'} mt={0.5}>
                                <strong>{vehicle.status === VehicleStatus.Rejected ? t('vehicleRegistrationDetail.rejectionReason') : t('vehicleRegistrationDetail.infoRequested')}</strong> {vehicle.statusNote}
                            </Typography>
                        )}
                    </Box>
                    <Chip label={statusDisplay.label} color={statusDisplay.color} size="small" />
                </Box>
                {rows.map((row, i) => (
                    <Box key={row.label}>
                        <Box sx={vehicleRegistrationRowStyles.row}>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>{row.label}</Typography>
                            {typeof row.value === 'string' ? (
                                <Typography variant="body2">{row.value}</Typography>
                            ) : row.value}
                        </Box>
                        {i < rows.length - 1 && <Divider />}
                    </Box>
                ))}
            </Paper>

            {/* Tlačítka dole */}
            {actionButtons && (
                <Box sx={registrationDetailStyles.bottomActions}>
                    {actionButtons}
                </Box>
            )}

            {/* Dialog: Zamítnout */}
            <Dialog open={showRejectModal} onClose={() => setShowRejectModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('vehicleRegistrationDetail.rejectDialogTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t('vehicleRegistrationDetail.rejectDialogText')}
                    </Typography>
                    <TextField
                        label={t('vehicleRegistrationDetail.rejectReasonLabel')}
                        multiline
                        rows={4}
                        fullWidth
                        placeholder={t('vehicleRegistrationDetail.rejectReasonPlaceholder')}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={registrationDetailStyles.dialogActions}>
                    <Button onClick={() => setShowRejectModal(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" color="error" onClick={handleReject}>{t('vehicleRegistrationDetail.rejectButton')}</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Vyžádat doplnění */}
            <Dialog open={showInfoModal} onClose={() => setShowInfoModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('vehicleRegistrationDetail.infoDialogTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t('vehicleRegistrationDetail.infoDialogText')}
                    </Typography>
                    <TextField
                        label={t('vehicleRegistrationDetail.infoMessageLabel')}
                        multiline
                        rows={4}
                        fullWidth
                        placeholder={t('vehicleRegistrationDetail.infoMessagePlaceholder')}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={registrationDetailStyles.dialogActions}>
                    <Button onClick={() => setShowInfoModal(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleRequestInfo}>{t('vehicleRegistrationDetail.infoSendButton')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RegistrationDetail;
